import Vuex from "vuex";
import pushNextPage from "@/utils/pushNextPage";
import updateCurrentPage from "@/utils/updateCurrentPage";
import fetchData from "@/utils/fetchData";
import debounce from "@/utils/debounce";
import { deleteDB } from "@/utils/manageIndexDb";
import auth from "./modules/auth";
import context from "./modules/context";
import output from "./modules/output";
import makes from "./modules/makes";
import wishes from "./modules/wishes";
import drawer from "./modules/drawer";
import format from "./modules/format";
import language from "./modules/language";
import length from "./modules/length";
import modals from "./modules/modals";
import personalInfo from "./modules/personalInfo";
import questions from "./modules/questions";
import result from "./modules/result";
import tabs from "./modules/tabs";
import tone from "./modules/tone";
import tours from "./modules/tours";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const store = new Vuex.Store({
  modules: {
    auth,
    context,
    output,
    makes,
    wishes,
    drawer,
    format,
    language,
    length,
    modals,
    personalInfo,
    questions,
    result,
    tabs,
    tone,
    tours,
  },
  state: {
    dbVersion: "1.2.0",
    prompt: "",
    promptError: "",
    promptHint: "",
    lastRequest: {},
    newInput: false,
    journeyId: "",
    // Below the computer layout, which starts at 1024px wide. Known from the start so
    // the first frame is laid out for the right screen.
    isMobile: window.matchMedia?.("(max-width:1023.98px)").matches ?? false,
  },
  mutations: {
    UPDATE_ENTIRE_STATE(state, { payload, dataToKeep = [] }) {
      for (const prop of Object.keys(payload)) {
        // don't replace data we want to keep
        if (dataToKeep.includes(prop)) continue;
        // Merge modules over their defaults, so pages saved before a field existed still work
        state[prop] =
          isPlainObject(state[prop]) && isPlainObject(payload[prop])
            ? { ...state[prop], ...payload[prop] }
            : payload[prop];
      }
    },
    SET_DB_VERSION(state, payload) {
      state.dbVersion = payload;
    },
    SET_PROMPT(state, payload) {
      // if payload starts with make me a, remove it from payload
      payload = payload.replace(
        /^(write me a|writemea|writeme|write me|write a|write)\s/i,
        ""
      );
      payload = payload.replace(
        /^(make me a|makemea|makeme|make me|make a|make)\s/i,
        ""
      );
      payload = payload.replace(
        /^(create me a|createmea|createme|create me|create a|create)\s/i,
        ""
      );
      payload = payload.replace(/^(me a)\s/i, "");
      // remove leading and trailing spaces
      payload = payload.trim();

      state.prompt = payload;
    },
    SET_PROMPT_ERROR(state, payload) {
      state.promptError = payload;
    },
    SET_PROMPT_HINT(state, payload) {
      state.promptHint = payload;
    },
    SET_LAST_REQUEST(state, payload) {
      state.lastRequest = payload;
    },
    SET_NEW_INPUT(state, payload) {
      state.newInput = payload;
    },
    SET_JOURNEY_ID(state) {
      state.journeyId = crypto.randomUUID();
    },
    SET_IS_MOBILE(state, payload) {
      state.isMobile = payload;
    },
  },
  actions: {
    resetLoading({ dispatch }) {
      dispatch("questions/resetLoading");
      dispatch("format/resetLoading");
      dispatch("tone/resetLoading");
      dispatch("result/resetLoading");
    },
    async checkVersion({ state }) {
      const { response } = await fetchData(
        "checkVersion",
        {},
        false,
        false,
        false
      );
      const version = response.result?.version;
      if (response.status !== 201 || !version || version === state.dbVersion) {
        return;
      }
      // Only reset once per version, so a mismatch can never cause a reload loop
      if (sessionStorage.getItem("resetForVersion") === version) {
        return;
      }
      sessionStorage.setItem("resetForVersion", version);
      await deleteDB();
      location.reload();
    },
    changePromptReset({ commit, dispatch }, prompt) {
      dispatch("modals/reset");
      dispatch("context/reset");
      dispatch("questions/reset");
      dispatch("format/reset");
      dispatch("tone/reset");
      dispatch("length/reset");
      dispatch("output/reset");
      dispatch("result/reset");
      commit("SET_NEW_INPUT", false);
      commit("SET_PROMPT_ERROR", "");
      commit("SET_PROMPT_HINT", "");
      commit("SET_PROMPT", prompt);
    },
    changePrompt(
      { state, commit, dispatch, rootState },
      { prompt, force = false }
    ) {
      if (!force && prompt.length === 0) {
        commit("SET_PROMPT_ERROR", "Please enter a something to make");
        return;
      }
      if (!force && prompt.length < 2) {
        commit(
          "SET_PROMPT_ERROR",
          "Please enter something 2 characters or longer"
        );
        return;
      }
      if (!force && prompt.length > 500) {
        commit("SET_PROMPT_ERROR", "Please enter something shorter");
        return;
      }
      if (!force && prompt.toLowerCase() === state.prompt.toLowerCase()) {
        commit("SET_PROMPT_ERROR", "Please enter a different prompt");
        return;
      }
      dispatch("changePromptReset", prompt);
      commit("SET_JOURNEY_ID");
      pushNextPage(rootState);
      dispatch("getRefinements");
      dispatch("checkPrompt");
      dispatch("drawer/createMake");
    },
    pageReset({ dispatch }) {
      dispatch("changePromptReset", "");
      dispatch("tabs/reset");
      dispatch("drawer/reset");
    },
    getRefinements({ dispatch }) {
      dispatch("questions/getQuestions");
      dispatch("tone/getTones");
      dispatch("format/getFormats");
    },
    async checkPrompt({ commit, state }) {
      const prompt = state.prompt;
      const { response } = await fetchData("generate/promptCheck", {});
      // Ignore answers for a prompt the user has already moved on from
      if (prompt !== state.prompt) return;
      if (response.status === 201 && response.result?.possible === false) {
        commit("SET_PROMPT_HINT", response.result.rewording || "");
      }
    },
    setLastRequest({ commit }, { url, result, status }) {
      const payload = {
        request: { url, timestamp: new Date().getTime() },
        response: { result, status },
      };
      commit("SET_LAST_REQUEST", payload);
      return payload;
    },
    setNewInput({ commit }, payload) {
      commit("SET_NEW_INPUT", payload);
    },
    setIsMobile({ commit, state }, payload) {
      if (state.isMobile === payload) {
        return;
      }
      commit("SET_IS_MOBILE", payload);
    },
  },
  getters: {},
});

// Save the page as it changes, without writing on every streamed token
const debouncedUpdateState = debounce((state) => {
  updateCurrentPage(state);
}, 250);

store.subscribe((mutation, state) => {
  if (mutation.type === "UPDATE_ENTIRE_STATE") {
    return;
  }
  debouncedUpdateState(state);
});

export default store;
